import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { searchOrdersApi } from "../../api/ordersApi";
import { updateUangJalanReportOrdersApi } from "../../api/uangJalanReportsApi";
import { TestId } from "../../constants/testIds";
import { OrderStatus, type Order } from "../../types/order";
import type { UangJalanReport } from "../../types/uangJalanReport";
import type translations from "../../translations/translations.json";
import { getErrorMessage } from "../../utils/errorHandling";
import { toastEmitter } from "../../utils/toastEmitter";

type TranslationKeys = keyof typeof translations.en;

interface AddOrdersToReportDialogProps {
  report: UangJalanReport;
  open: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

const AddOrdersToReportDialog: React.FC<AddOrdersToReportDialogProps> = ({
  report,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [availableOrders, setAvailableOrders] = useState<Order[]>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existingOrderIds = useMemo(() => new Set(report.orderIds), [report.orderIds]);

  useEffect(() => {
    if (!open) {
      setSelectedOrderIds([]);
      setError(null);
      return;
    }

    const loadOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const { orders } = await searchOrdersApi(
          {
            teamOperationsId: report.teamOperationsId,
            status: OrderStatus.READY_FOR_REVIEW,
          },
          { page: 1, pageSize: 100 },
        );
        setAvailableOrders(
          orders.filter((order) => !existingOrderIds.has(order._id) && !order.uangJalanReportId),
        );
      } catch (err: any) {
        setError(getErrorMessage(err, t, "fetchOrders"));
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [open, report.teamOperationsId, existingOrderIds, t]);

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrderIds((current) =>
      current.includes(orderId) ? current.filter((id) => id !== orderId) : [...current, orderId],
    );
  };

  const handleSubmit = async () => {
    if (selectedOrderIds.length === 0) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await updateUangJalanReportOrdersApi(report._id, {
        orderIds: [...report.orderIds, ...selectedOrderIds],
      });
      await onSuccess();
      toastEmitter.emit(t("reports.addOrdersSuccessfully" as TranslationKeys), "success");
      onClose();
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "updateUangJalanReport");
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid={TestId.UJR.ADD_ORDERS_DIALOG}
    >
      <DialogTitle>{t("reports.addOrdersTitle" as TranslationKeys)}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : availableOrders.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("reports.noOrdersToAdd" as TranslationKeys)}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
            {availableOrders.map((order) => (
              <FormControlLabel
                key={order._id}
                control={
                  <Checkbox
                    checked={selectedOrderIds.includes(order._id)}
                    onChange={() => toggleOrderSelection(order._id)}
                    slotProps={{
                      input: {
                        "data-testid": TestId.UJR.ADD_ORDER_CHECKBOX(order._id),
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                }
                label={`${order.externalOrderId || order._id} — ${order.customer?.name || "—"}`}
              />
            ))}
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Cancel" as TranslationKeys)}</Button>
        <Button
          variant="contained"
          disabled={submitting || selectedOrderIds.length === 0}
          onClick={handleSubmit}
          data-testid={TestId.UJR.ADD_ORDERS_SUBMIT}
        >
          {t("reports.addOrders" as TranslationKeys)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrdersToReportDialog;
